import type { PImage } from '../engine/aliases'
import { assert } from '../lib/assert-sidecar'
import { common } from '../main/common-sidecar'
import type { IAnimDataFrame } from './animsData'
import type { ILargeSprite } from './largeSprites'
import type { ISPD } from './sprites'

export interface IFrameManual {
  x: number
  y: number
  w: number
  h: number
}

let defaultScale = 4
export const setDefaultScale = (scale: number) => {
  defaultScale = scale
}

export const destroyImages = (images: PImage[]) => {
  for (let i = 0; i < images.length; i++) {
    images[i].destroy()
  }
}

export const setPackedFrame = (image: PImage, fi: ISPD) => {
  let frame = getPackedFrame(fi)
  image.frame = frame
  image.setScale(defaultScale / (fi.s || 1))
  image.setDisplayOrigin(frame.width * 0.5, frame.height * 0.5)
  image.fi = fi
}

export const setManualFrame = (image: PImage, fi: IFrameManual) => {
  let frame = createManualFrame(fi, image.texture)
  image.frame = frame
  // image.setScale(defaultScale)
  // image.setDisplayOrigin(frame.width * 0.5, frame.height * 0.5)
}

export const scale = (image: PImage, scale: number) => {
  if (image.fi && image.fi.s) {
    scale = scale / image.fi.s
  }
  image.scale = scale
}

export const setPackedFrameProgress = (
  image: PImage,
  fis: ISPD[],
  progress: number,
) => {
  let frameNum = Math.round(fis.length * progress)
  if (frameNum < 0) {
    frameNum = 0
  }
  if (frameNum >= fis.length) {
    frameNum = fis.length - 1
  }
  setPackedFrame(image, fis[frameNum])
}

export const copyXY = (dest: PImage, src: PImage) => {
  dest.x = src.x
  dest.y = src.y
}

let manualFrameIdx = 100
export function createManualFrame(
  fi: IFrameManual,
  tex: Phaser.Textures.Texture,
) {
  let f = new Phaser.Textures.Frame(
    tex,
    `mframe-${++manualFrameIdx}`,
    0,
    fi.x,
    fi.y,
    fi.w,
    fi.h,
  )
  return f
}

let _animFrameNum = 0
export function createAnimFrame(
  tex: Phaser.Textures.Texture,
  fd: IAnimDataFrame,
) {
  let f = new Phaser.Textures.Frame(
    tex,
    _animFrameNum++,
    0,
    fd.px,
    fd.py,
    fd.pw,
    fd.ph,
  )
  f.setTrim(fd.sw, fd.sh, fd.tx, fd.ty, fd.tw, fd.th)
  return f
}

export function createAnimImage(
  layer: Phaser.GameObjects.Container,
  // frame: Phaser.Textures.Frame,
) {
  let scene = common.getPhaserScene()
  let image = scene.add.image(0, 0, getSspTexture())
  layer.add(image)
  image.setOrigin(0, 0)
  image.width = 10
  image.height = 10

  // image.frame = frame
  image.originX = 0.5
  image.originY = 0.5
  image.x = 0
  image.y = 0
  image.scale = defaultScale
  // image.setDisplayOrigin(frame.width / 2, frame.height / 2)
  return image
}

let _sspTexture: Phaser.Textures.Texture | null
export function getSspTexture() {
  if (!_sspTexture) {
    let scene = common.getPhaserScene()
    _sspTexture = scene.game.textures.get('ssp')
    assert.notNil(_sspTexture)
  }
  return _sspTexture
}

let __sspFrameIndex = 0
export function getPackedFrame(fi: ISPD) {
  assert.notNil(fi)
  let frame = fi.f
  if (!frame) {
    __sspFrameIndex++
    if (fi.t === 1) {
      //} && fi.x === sprites.underIconHealth[0].x) {
      // console.log('loaded trim', fi)
      let f = new Phaser.Textures.Frame(
        getSspTexture(),
        __sspFrameIndex, //`ssp-${fi.p}-${fi.x}-${fi.y}-${fi.w}-${fi.h}`,
        0,
        fi.x + (fi.tx || 0),
        fi.y + (fi.ty || 0),
        fi.tw || 0,
        fi.th || 0,
        // fi.tx || 0,
        // fi.ty || 0,
        // fi.tw || 0,
        // fi.th || 0,
      )
      // f.setTrim(fi.w, fi.h, fi.tx || 0, fi.ty || 0, fi.tw || 0, fi.th || 0)
      //f.setTrim(fi.w, fi.x, fi.tx || 0, fi.ty || 0, fi.tw || 0, fi.th || 0)
      f.setTrim(fi.w, fi.h, fi.tx || 0, fi.ty || 0, fi.w, fi.h)
      fi.f = f
      return f
    } else {
      let f = new Phaser.Textures.Frame(
        getSspTexture(),
        __sspFrameIndex, //`ssp-${fi.p}-${fi.x}-${fi.y}-${fi.w}-${fi.h}`,
        0,
        fi.x,
        fi.y,
        fi.w,
        fi.h,
      )
      fi.f = f
      return f
    }
  }
  return frame
}

export function createImage(layer: Phaser.GameObjects.Container, fi: ISPD) {
  let img = createImageFromFrame(layer, getPackedFrame(fi))
  img.fi = fi
  if (fi.s) {
    img.scale = img.scale / fi.s
  }
  return img
}

export function createMixed(
  layer: Phaser.GameObjects.Container,
  fi: ISPD | null,
  largeSprite: ILargeSprite | null,
): PImage {
  assert.f(fi != null && largeSprite != null, 'you cannot have everything')
  if (fi) {
    return createImage(layer, fi)
  }
  if (largeSprite) {
    return createLargeSprite(layer, largeSprite)
  }
  assert.failed('no valid data')
  throw new Error('!')
}

export function createImageFromFrame(
  layer: Phaser.GameObjects.Container,
  frame: Phaser.Textures.Frame,
): PImage {
  let scene = common.getPhaserScene()
  let image = scene.add.image(0, 0, '')
  layer.add(image)
  image.width = frame.width
  image.height = frame.height
  image.frame = frame
  image.originX = 0.5
  image.originY = 0.5
  image.x = 0
  image.y = 0
  image.scale = defaultScale
  image.setDisplayOrigin(frame.width / 2, frame.height / 2)
  return image
}

export function createBigImage(
  layer: Phaser.GameObjects.Container,
  tex: string,
) {
  let scene = common.getPhaserScene()
  let image = scene.add.image(0, 0, tex)
  image.setOrigin(0, 0)

  image.x = 0
  image.y = 0
  image.scale = defaultScale
  layer.add(image)
  return image
}

export function createLargeSprite(
  layer: Phaser.GameObjects.Container,
  ls: ILargeSprite,
) {
  let scene = common.getPhaserScene()
  let image = scene.add.image(0, 0, ls.k)
  image.setOrigin(0, 0)
  image.x = 0
  image.y = 0
  image.scale = defaultScale
  layer.add(image)
  return image
}
